import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Text from 'components/typography/text/Text';
import Heading from 'components/typography/heading/Heading';
import Icon from 'components/icon/Icon';
import Button from 'components/button/Button';
import {
  accountSettingsTwoFactorResendEmailInitial,
  accountSettingsTwoFactorCheck,
  accountSettingsTwoFactorResendEmail,
  openEditPasswordFormAction,
  openEditEmailFormAction,
} from 'store/account/accountSettingsActions';

import './AccountSettingsTwoFactor.scss';
import { RootState } from 'store/rootState';

import { AccountSettingsTwoFactorProps } from './AccountSettingsTwoFactor.types';

const AccountSettingsTwoFactor: FC<AccountSettingsTwoFactorProps> = ({
  title,
  text,
  type,
}) => {
  const dispatch = useDispatch();
  const email = useSelector((state: RootState) => state.auth.user.email);
  const resendEmail = useSelector(
    (state: RootState) => state.accountSettings.two_factor_resend_email,
  );
  const authenticated = useSelector(
    (state: RootState) => state.accountSettings.two_factor_is_set,
  );

  useEffect(() => {
    dispatch(accountSettingsTwoFactorCheck(true));

    const interval = setInterval(() => {
      dispatch(accountSettingsTwoFactorCheck(false));
    }, 5000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const continueClick = (type: string | null) => {
    dispatch(accountSettingsTwoFactorResendEmailInitial());

    if (type === 'password') {
      return dispatch(openEditPasswordFormAction());
    }
    return dispatch(openEditEmailFormAction());
  };
  const resendingEmail = (resendEmail: string) => {
    let resendEmailContents;
    switch (resendEmail) {
      case 'processing':
        resendEmailContents = 'Resending e-mail...';
        break;
      case 'success':
        resendEmailContents = 'E-mail has been sent again';
        break;
      case 'failed':
        resendEmailContents = (
          <>
            Resending e-mail failed.
            <Button
              onClick={() => dispatch(accountSettingsTwoFactorResendEmail())}
              scheme="link"
              text="Try again"
              size="inline"
              hasShadow={false}
            />
          </>
        );
        break;
      default:
        resendEmailContents = (
          <>
            No e-mail received yet?{' '}
            <Button
              onClick={() => dispatch(accountSettingsTwoFactorResendEmail())}
              scheme="link"
              text="Click here to resend confirmation e-mail."
              size="inline"
              hasShadow={false}
            />
          </>
        );
    }

    return (
      <Text size="small" color="grey-dark" flatten>
        {resendEmailContents}
      </Text>
    );
  };

  return (
    <>
      <div className="account-settings-two-factor__fields-container">
        <Heading level={1} stylingLevel={2}>
          {title}
        </Heading>
        <Icon
          name="twoFactorAuthentication"
          className="account-settings-two-factor__icon"
          block={false}
        />
        {authenticated ? (
          <>
            <Icon
              name="check"
              size="small"
              color="green"
              className="account-settings-two-factor__verification-icon"
            />
            <Text tag="span" size="small">
              Verification successful
            </Text>
          </>
        ) : (
          <>
            <Text size="small">Email sent to: {email}</Text>
            <Text size="small" color="grey-dark" flatten>
              {text}
            </Text>
            {resendingEmail(resendEmail)}
          </>
        )}
      </div>

      {authenticated ? (
        <Button
          tag="button"
          size="large"
          color="green"
          className="account-settings-two-factor__next-button"
          text="Continue"
          handler={() => continueClick(type)}
          block
        />
      ) : (
        <Button
          tag="button"
          size="large"
          className="account-settings-two-factor__next-button"
          text="Waiting for authentication…"
          block
        />
      )}
    </>
  );
};

export default AccountSettingsTwoFactor;
